import { inject, Injectable, OnDestroy } from '@angular/core';
import { MonitoringService, TrackingService } from '@celerofinancas/service-utils';
import { CompanyState, UserState } from '@celerofinancas/state-management';
import { environment } from '@environments/environment';
import { Store } from '@ngxs/store';
import { combineLatest } from 'rxjs';
import { filter, take } from 'rxjs/operators';

/**
 * Recursive stable JSON serializer. Object property keys are sorted
 * alphabetically at every depth so that semantically identical payloads
 * produce the same string regardless of the order in which their keys were
 * inserted. Array order is preserved because it carries meaning.
 *
 * @param value - Any JSON-compatible value (primitive, array, plain object,
 *   `null`, or `undefined`).
 * @returns Deterministic string representation suitable for dedup keys.
 */
function stableStringify(value: unknown): string {
  if (value === null || value === undefined || typeof value !== 'object') {
    return JSON.stringify(value);
  }

  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(',')}]`;
  }

  const entries = Object.keys(value as Record<string, unknown>)
    .sort()
    .map((key) => `${JSON.stringify(key)}:${stableStringify((value as Record<string, unknown>)[key])}`);

  return `{${entries.join(',')}}`;
}

/**
 * Tracking event queued while NGXS state has not yet hydrated.
 */
interface PendingEvent {
  /**
   * Event name forwarded to the underlying tracking provider.
   */
  event: unknown;

  /**
   * Optional payload merged into the default session and action metadata.
   */
  action?: Record<string, unknown>;
}

/**
 * TrackingService replacement that buffers events fired before NGXS state hydration.
 *
 * PR #162 removed the NgxsStoragePluginModule from this CL, so UserState and
 * CompanyState no longer rehydrate synchronously on app boot. Page-view tracks
 * fired in lib component ngOnInit run before the `me/read` and `company/by/...`
 * requests complete, sending payloads with undefined `distinct_id` that the
 * tracking backend drops. Click and filter events fire after hydration and
 * survive.
 *
 * Events queued here are flushed in FIFO order once UserState.pk and the
 * CompanyState active company pk are both present.
 *
 * The buffer also deduplicates events: identical `event` and `action` pairs
 * queued while pending collapse into one entry, and the same pair dispatched
 * more than once inside a short window after flush is suppressed. This guards
 * against duplicate ngOnInit firings and rapid re-renders during boot.
 */
@Injectable({ providedIn: 'root' })
export class BufferedTrackingService extends TrackingService implements OnDestroy {
  /**
   * Time window in milliseconds during which an identical event dispatched
   * after flush is suppressed.
   */
  private static readonly POST_FLUSH_DEDUP_MS = 2000;

  /**
   * NGXS store used to observe user and company hydration.
   */
  private readonly hydrationStore = inject(Store);

  /**
   * FIFO queue of events captured before hydration completes.
   */
  private readonly pending: PendingEvent[] = [];

  /**
   * Last dispatch timestamp per dedup key, used to suppress duplicate events
   * fired within `POST_FLUSH_DEDUP_MS` of the previous identical dispatch.
   */
  private readonly recentDispatch = new Map<string, number>();

  /**
   * Whether the pending queue has been drained. After flush, track bypasses
   * the buffer and forwards events to the parent implementation directly.
   */
  private flushed = false;

  /**
   * Monitoring provider used to report queue loss on unload. Independent of
   * the tracking pipeline itself, since the whole point is to observe cases
   * where that pipeline never hydrates.
   */
  private readonly monitoring = new MonitoringService();

  /**
   * Initializes the buffered tracking service.
   *
   * When tracking is disabled in the environment, marks the buffer as flushed
   * so subsequent calls fall through to the parent implementation, which
   * already short-circuits on disabled tracking.
   *
   * When tracking is enabled, subscribes once to the user and company state to
   * drain the buffer as soon as both are populated, and starts observing tab
   * visibility to report cases where the tab closes with events still queued.
   */
  constructor() {
    super();

    if (!environment?.tracking?.enable) {
      this.flushed = true;
      return;
    }

    combineLatest([
      this.hydrationStore.select(UserState.getState),
      this.hydrationStore.select(CompanyState.getActiveCompanyPk),
    ])
      .pipe(
        filter(([user, companyPk]) => !!user?.pk && !!companyPk),
        take(1),
      )
      .subscribe(() => this.flushPendingEvents());

    document.addEventListener('visibilitychange', this.onVisibilityChange);
    window.addEventListener('pagehide', this.onPageHide);
  }

  /**
   * Bound listeners kept as fields so they can be removed on destroy.
   */
  private readonly onVisibilityChange = (): void => {
    if (document.visibilityState === 'hidden') this.reportPendingLossOnUnload();
  };
  private readonly onPageHide = (): void => this.reportPendingLossOnUnload();

  /**
   * Removes the unload listeners registered in the constructor.
   */
  public ngOnDestroy(): void {
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    window.removeEventListener('pagehide', this.onPageHide);
  }

  /**
   * Reports the current pending queue to monitoring when the tab is being
   * hidden or closed while hydration never finished. Fase 1 observability for
   * D2: measures how often events queued before hydration are about to be
   * lost, without depending on the same tracking pipeline that is stuck.
   */
  private reportPendingLossOnUnload(): void {
    if (this.flushed || this.pending.length === 0) return;

    this.monitoring.logError(new Error('Buffered tracking queue lost on unload'), {
      transactionName: 'buffered_tracking_queue_lost',
      extra: {
        pendingCount: this.pending.length,
        events: this.pending.map(({ event }) => String(event)),
      },
    });
  }

  /**
   * Queues the event when hydration has not finished, otherwise forwards it to
   * the parent tracking service. Duplicate events are suppressed both inside
   * the pending queue and inside a short window after flush.
   *
   * @param event - Event name or tracking model forwarded to the provider.
   * @param action - Optional payload merged into the default session and action.
   */
  public override track<T = unknown>(event: T, action?: Record<string, unknown>): void {
    const key = this.dedupKey(event, action);

    if (!this.flushed) {
      if (this.isPendingDuplicate(key)) return;
      this.pending.push({ event, action });
      return;
    }

    if (this.isWithinDedupWindow(key)) return;
    this.dispatch(event, action, key);
  }

  /**
   * Marks the buffer as flushed and dispatches every queued event in order to
   * the parent tracking service.
   */
  private flushPendingEvents(): void {
    this.flushed = true;
    const buffered = this.pending.splice(0);
    buffered.forEach(({ event, action }) => {
      const key = this.dedupKey(event, action);
      this.dispatch(event, action, key);
    });
  }

  /**
   * Forwards the event to the parent tracking service and records its dispatch
   * timestamp so subsequent identical events inside the dedup window are
   * suppressed.
   *
   * @param event - Event name or tracking model forwarded to the provider.
   * @param action - Optional payload merged into the default session and action.
   * @param key - Precomputed dedup key for the event and action pair.
   */
  private dispatch<T = unknown>(
    event: T,
    action: Record<string, unknown> | undefined,
    key: string,
  ): void {
    this.recentDispatch.set(key, Date.now());
    super.track(event as never, action);
  }

  /**
   * Builds a deterministic dedup key from an event and its action payload.
   * Uses `stableStringify` so semantically identical payloads with different
   * property insertion orders collapse to the same key.
   *
   * @param event - Event name or tracking model.
   * @param action - Optional payload merged into the default session and action.
   * @returns Composite key in the form `${event}::${stableStringify(action)}`.
   */
  private dedupKey(event: unknown, action?: Record<string, unknown>): string {
    return `${String(event)}::${stableStringify(action ?? null)}`;
  }

  /**
   * Checks whether an event with the same dedup key is already waiting in the
   * pending queue.
   *
   * @param key - Dedup key produced by `dedupKey`.
   * @returns True when the key is already enqueued.
   */
  private isPendingDuplicate(key: string): boolean {
    return this.pending.some(({ event, action }) => this.dedupKey(event, action) === key);
  }

  /**
   * Checks whether the last dispatch with the given key happened inside the
   * post-flush dedup window. Expired entries are removed on read so the map
   * does not grow unbounded.
   *
   * @param key - Dedup key produced by `dedupKey`.
   * @returns True when the previous dispatch is still inside the dedup window.
   */
  private isWithinDedupWindow(key: string): boolean {
    const last = this.recentDispatch.get(key);

    if (last === undefined) return false;

    if (Date.now() - last <= BufferedTrackingService.POST_FLUSH_DEDUP_MS) return true;

    this.recentDispatch.delete(key);
    return false;
  }
}
